import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { listSettings, saveSetting, MIGRATION_MISSING } from "../../../lib/adminApi";
import { invalidateSettingsCache } from "../../../lib/settings";
import { MigrationNotice } from "../../../components/admin/MigrationNotice";

const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-3 py-2.5 text-sm text-[#404040] focus:outline-none focus:border-[#B09060]";
const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-[#8F7648] mb-1";

const FIELDS = [
  { key: "whatsapp", label: "WhatsApp Bağlantısı", hint: "https://wa.me/90XXXXXXXXXX biçiminde" },
  { key: "phone", label: "Telefon" },
  { key: "email", label: "E-posta" },
  { key: "address", label: "Adres" },
  { key: "hours", label: "Çalışma Saatleri" },
  { key: "instagram", label: "Instagram Bağlantısı", hint: "https://www.instagram.com/kullaniciadi/ biçiminde" },
  { key: "hero_label", label: "Ana Sayfa Üst Etiket", hint: "Hero bölümündeki küçük başlık" },
  { key: "hero_subline", label: "Ana Sayfa Alt Başlık", hint: "Hero bölümündeki açıklama metni", textarea: true },
];

export default function SettingsSection() {
  const [values, setValues] = useState({});
  const [state, setState] = useState("loading");
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const rows = await listSettings();
      const map = {};
      rows.forEach((r) => { map[r.key] = r.value; });
      setValues(map);
      setState("ready");
    } catch (e) {
      setState(e === MIGRATION_MISSING ? "missing" : "ready");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(key) {
    setSaving(key);
    try {
      await saveSetting(key, values[key] ?? "");
      invalidateSettingsCache();
      toast.success("Kaydedildi — siteye anında yansıyacak.");
    } catch (err) {
      toast.error(err.message || "Kaydedilemedi.");
    }
    setSaving(null);
  }

  if (state === "missing") return <MigrationNotice section="Site Ayarları" />;
  if (state === "loading") return <div className="text-[#6B6B6B] text-sm py-10" data-testid="settings-loading">Yükleniyor...</div>;

  return (
    <div data-testid="settings-section" className="max-w-2xl">
      <h2 className="font-serif-display text-2xl text-[#404040]">Site Ayarları</h2>
      <p className="text-sm text-[#6B6B6B] mt-1">
        İletişim bilgileri ve site metinleri — kaydettiğinizde web sitesi otomatik güncellenir.
      </p>

      <div className="space-y-5 mt-8">
        {FIELDS.map((f) => (
          <div key={f.key} className="bg-white border border-[#B09060]/20 rounded-sm p-5 shadow-[0_4px_20px_rgba(176,144,96,0.05)]">
            <label className={labelCls}>{f.label}</label>
            {f.textarea ? (
              <textarea
                rows={3}
                className={inputCls}
                data-testid={`setting-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              />
            ) : (
              <input
                className={inputCls}
                data-testid={`setting-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              />
            )}
            {f.hint && <p className="text-[11px] text-[#9B9B8F] mt-1.5">{f.hint}</p>}
            <button
              onClick={() => save(f.key)}
              disabled={saving === f.key}
              data-testid={`setting-save-${f.key}`}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#B09060] text-white text-xs font-semibold uppercase tracking-wider hover:bg-[#8F7648] transition-colors disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {saving === f.key ? "..." : "Kaydet"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
